import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { type CopilotSettings, getCopilotSettings, updateCopilotSettings } from '../../lib/api';

/**
 * Per-tenant AI Copilot configuration page.
 *
 * Toggles per-feature, brand-voice notes free-text, monthly token cap, and
 * the live usage gauge.
 */
export function CopilotSettingsPage(): ReactElement {
  const [settings, setSettings] = useState<CopilotSettings | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCopilotSettings()
      .then(setSettings)
      .catch((e) => setError(String(e)));
  }, []);

  if (!settings) return <p className="px-6 py-8 text-sm">Loading…</p>;

  const pct =
    settings.monthlyTokenCap > 0
      ? Math.min(100, Math.round((settings.tokensUsedThisMonth / settings.monthlyTokenCap) * 100))
      : 0;

  const onChange = <K extends keyof CopilotSettings>(key: K, value: CopilotSettings[K]) => {
    setSettings({ ...settings, [key]: value });
    setDirty(true);
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateCopilotSettings({
        isEnabled: settings.isEnabled,
        dailyBriefAiSummary: settings.dailyBriefAiSummary,
        customerReplyDrafts: settings.customerReplyDrafts,
        noShowPredictions: settings.noShowPredictions,
        anomalyAlerts: settings.anomalyAlerts,
        monthlyTokenCap: settings.monthlyTokenCap,
        brandVoiceNotes: settings.brandVoiceNotes,
      });
      setSettings({
        ...updated,
        ...(settings.aiConfigured !== undefined ? { aiConfigured: settings.aiConfigured } : {}),
      });
      setDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = (label: string, helper: string, key: keyof CopilotSettings & string) => (
    <label className="flex items-start gap-3 py-3">
      <input
        type="checkbox"
        checked={(settings[key] as number) === 1}
        onChange={(e) =>
          onChange(key as keyof CopilotSettings, (e.target.checked ? 1 : 0) as never)
        }
        className="mt-1"
      />
      <div>
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-muted-foreground">{helper}</div>
      </div>
    </label>
  );

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">AI Ops Copilot settings</h1>
      </header>

      {!settings.aiConfigured && (
        <div className="mb-6 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          AI Copilot is not enabled. Set <code>ANTHROPIC_API_KEY</code> via{' '}
          <code>wrangler secret put ANTHROPIC_API_KEY</code> to activate.
        </div>
      )}

      <section className="mb-6 rounded border border-border bg-card p-4">
        <h2 className="mb-1 text-sm font-medium">Monthly usage</h2>
        <div className="text-xs text-muted-foreground">
          <span className="num">{settings.tokensUsedThisMonth.toLocaleString()}</span> of{' '}
          <span className="num">{settings.monthlyTokenCap.toLocaleString()}</span> tokens. Resets{' '}
          {new Date(settings.monthResetAt).toLocaleDateString()}.
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded bg-muted">
          <div
            className={`h-full ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </section>

      <section className="mb-6 rounded border border-border bg-card p-4">
        <h2 className="mb-2 text-sm font-medium">Features</h2>
        <div className="divide-y divide-border">
          {toggleRow(
            'AI enabled',
            'Master switch. Off = nothing AI-powered runs for this tenant.',
            'isEnabled',
          )}
          {toggleRow(
            'Daily brief AI summary',
            'Adds a 2-paragraph natural-language summary to the staff daily brief email.',
            'dailyBriefAiSummary',
          )}
          {toggleRow(
            'Customer reply drafts',
            'Drafts a customer reply on the staff message composer.',
            'customerReplyDrafts',
          )}
          {toggleRow(
            'No-show predictions',
            'Surfaces risk score + reasons on upcoming bookings.',
            'noShowPredictions',
          )}
          {toggleRow(
            'Anomaly alerts',
            'Daily comparison of revenue, occupancy, refund rate vs trailing 28d.',
            'anomalyAlerts',
          )}
        </div>
      </section>

      <section className="mb-6 rounded border border-border bg-card p-4">
        <h2 className="mb-1 text-sm font-medium">Brand voice notes</h2>
        <p className="mb-2 text-xs text-muted-foreground">
          Free-text guidance the AI applies to every generated response. Up to 4000 chars.
        </p>
        <textarea
          value={settings.brandVoiceNotes ?? ''}
          onChange={(e) => onChange('brandVoiceNotes', e.target.value || null)}
          className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
          rows={5}
          placeholder='e.g. "Warm, professional, plain English. Sign as The Levern Bank team. Never use exclamation marks."'
        />
      </section>

      <section className="mb-6 rounded border border-border bg-card p-4">
        <h2 className="mb-1 text-sm font-medium">Monthly token cap</h2>
        <p className="mb-2 text-xs text-muted-foreground">
          When the counter reaches the cap, all AI features refuse with a 402 until the start of the
          next month. Useful for cost control.
        </p>
        <input
          type="number"
          min={0}
          step={10000}
          value={settings.monthlyTokenCap}
          onChange={(e) =>
            onChange('monthlyTokenCap', Math.max(0, Math.floor(Number(e.target.value))))
          }
          className="w-48 rounded border border-border bg-background px-3 py-2 text-sm num"
        />
      </section>

      {error && <p className="mb-2 text-sm text-red-700">{error}</p>}
      <button
        type="button"
        disabled={!dirty || saving}
        onClick={onSave}
        className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
